<template>
  <div
    class="radio-group"
    :class="{ 'radio-group--invalid': isInvalid }"
  >
    <div
      class="radio-group__items"
      :class="{ [`radio-group__items--${props.direction}`]: true }"
    >
      <div
        v-for="item in $props.items"
        :key="item.value"
      >
        <basic-radio
          v-model="model"
          :disabled="$props.disabled && item.value !== model"
          :label="item.text"
          :is-invalid="isInvalid"
          :name="$props.name"
          :value="item.value"
          @update:modelValue="emitUpdate(item.value)"
        />
      </div>
    </div>
    <div
      v-if="errorHint"
      class="radio-group__error-text caption-regular"
    >
      <the-icon
        art="solid"
        name="circle-exclamation"
        class="error-icon"
      />
      <span v-text="errorHint" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import { logWarning } from '@/helpers/Logger'

import useI18n from '@/hooks/useI18n'

import BasicRadio from '@/components/Basic/Radio'
import TheIcon from '@/components/Container/TheIcon'

// HOOKS
const { t } = useI18n()

// INIT
const model = defineModel({ type: [String, Number, Boolean] })
const props = defineProps({
  direction: {
    type: String,
    default: 'row',
    validator(val) {
      return ['column', 'row'].includes(val)
    },
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  items: {
    type: Array,
    required: true,
    validator(val) {
      const isValidObj = val.every(obj => Object.hasOwn(obj, 'value') && Object.hasOwn(obj, 'text'))
      if (!isValidObj) {
        logWarning('[Vue Warn]: Invalid prop: items should have the following properties: {value, text}')
        return false
      }
      return true
    },
  },

  manualValidation: {
    type: Boolean,
    default: false,
  },

  name: {
    type: String,
    required: true,
  },

  supportingText: {
    type: String,
    default: undefined,
  },

  v: {
    type: Object,
    default: () => {},
  },
})

// COMPUTED
const errorHint = computed(() => {
  if (isInvalid.value) {
    const error = props.v?.$errors[0]?.$validator
    if (error) return t(`form.error.${error}`)
  }
  return props.supportingText
})

const isInvalid = computed(() => {
  return !!props.v?.$invalid && !!props.v?.$error
})

// METHODS
function emitUpdate(value) {
  if (!props.manualValidation) props.v?.$validate()
  model.value = value
}
</script>

<style scoped>
.radio-group {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-03);
}
.radio-group__items {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-03);

  &--row {
    flex-direction: row;
    gap: var(--fixed-spacing-fix-10);
  }
}

.radio-group__error-text {
  color: var(--error);
  height: var(--line-height-regular-xs);
  span {
    padding-left: var(--fixed-spacing-fix-01);
  }
}
</style>
